interface StorageItem<T> {
  data: T;
  timestamp: number;
  version: number;
}

export class StorageManager {
  private static instance: StorageManager;
  private prefix = 'hvga_';
  private version = 1;

  private constructor() {}

  static getInstance(): StorageManager {
    if (!StorageManager.instance) {
      StorageManager.instance = new StorageManager();
    }
    return StorageManager.instance;
  }

  save<T>(key: string, data: T): boolean {
    try {
      const item: StorageItem<T> = {
        data,
        timestamp: Date.now(),
        version: this.version
      };
      localStorage.setItem(this.prefix + key, JSON.stringify(item));
      return true;
    } catch (error) {
      console.error(`Failed to save data for key ${key}:`, error);
      this.handleStorageError();
      return false;
    }
  }

  get<T>(key: string): T | null {
    try {
      const itemStr = localStorage.getItem(this.prefix + key);
      if (!itemStr) return null;
      
      const item: StorageItem<T> = JSON.parse(itemStr);
      return item.data;
    } catch (error) {
      console.error(`Failed to get data for key ${key}:`, error);
      return null;
    }
  }

  remove(key: string): boolean {
    try {
      localStorage.removeItem(this.prefix + key);
      return true;
    } catch (error) {
      console.error(`Failed to remove data for key ${key}:`, error);
      return false;
    }
  }

  private handleStorageError() {
    // Try to free up space by removing old data
    this.cleanupOldData();
  }

  private cleanupOldData() {
    try {
      // Get all keys with our prefix
      const keys = Object.keys(localStorage).filter(key => key.startsWith(this.prefix));
      
      // Sort by last accessed time (if we had that info)
      // For now, just remove oldest 20% of items if we have more than 50
      if (keys.length > 50) {
        const keysToRemove = keys.slice(0, Math.ceil(keys.length * 0.2));
        keysToRemove.forEach(key => localStorage.removeItem(key));
        console.log(`Cleaned up ${keysToRemove.length} old storage items`);
      }
    } catch (error) {
      console.error('Failed to cleanup storage:', error);
    }
  }
}

// Convenience functions
export const saveToStorage = <T>(key: string, data: T): boolean => {
  return StorageManager.getInstance().save(key, data);
};

export const getFromStorage = <T>(key: string): T | null => {
  return StorageManager.getInstance().get<T>(key);
};

export const removeFromStorage = (key: string): boolean => {
  return StorageManager.getInstance().remove(key);
}; 